/**
 * leagueSync.ts
 * ─────────────
 * Client-side multiplayer sync layer for WBBL.
 *
 * How it works
 * ────────────
 * 1.  On load, fetch the canonical league snapshot from the WBBL API.
 * 2.  Compare the server version with the local IndexedDB version.
 *     If the server is newer, replace the local copy.
 * 3.  Open a WebSocket to /api/league/live.
 *     When the server broadcasts an update, re-fetch the snapshot and
 *     notify all registered subscribers.
 * 4.  Before any league-modifying action, call `sendAction()` which signs
 *     the action with the user's MetaMask wallet and POSTs it to the API.
 *     The server validates ownership (NFT / claims map) and broadcasts.
 *
 * Configuration
 * ─────────────
 * Set WBBL_API_URL in the environment / build config.
 * Defaults to https://wbbl-api.wbbl.workers.dev in production.
 */

// ── config ───────────────────────────────────────────────────────────────────

const API_BASE: string =
	(typeof import.meta !== "undefined" && (import.meta as any).env?.WBBL_API_URL) ||
	"https://wbbl-api.wbbl.workers.dev";

// ── types ────────────────────────────────────────────────────────────────────

export type SyncStatus = "connecting" | "live" | "stale" | "offline";

export interface LeagueAction {
	type: ActionType;
	payload: Record<string, unknown>;
}

export type ActionType =
	| "roster_move"
	| "depth_chart"
	| "sign_fa"
	| "waive_player"
	| "propose_trade"
	| "claim_team";

type UpdateListener = (version: number) => void;
type StatusListener = (status: SyncStatus) => void;

// ── internal state ────────────────────────────────────────────────────────────

let _ws: WebSocket | null = null;
let _version = 0;
let _status: SyncStatus = "offline";
let _nonce = Date.now();
let _reconnectTimer: ReturnType<typeof setTimeout> | null = null;

const _updateListeners: Set<UpdateListener> = new Set();
const _statusListeners: Set<StatusListener> = new Set();

// ── public API ────────────────────────────────────────────────────────────────

/**
 * Initialise the sync layer.
 * Call once when the crypto league UI mounts.
 * Returns the current server version (0 if the server has no state yet).
 */
export async function initLeagueSync(): Promise<number> {
	await _fetchVersion();
	_connectWs();
	return _version;
}

/** Stop all sync activity (call on unmount / navigation away). */
export function destroyLeagueSync() {
	if (_reconnectTimer) clearTimeout(_reconnectTimer);
	if (_ws) {
		_ws.onclose = null;
		_ws.close();
		_ws = null;
	}
	_setStatus("offline");
}

/** Register a callback that fires when the server has a new snapshot version. */
export function onLeagueUpdate(cb: UpdateListener): () => void {
	_updateListeners.add(cb);
	return () => _updateListeners.delete(cb);
}

/** Register a callback that fires when the WebSocket connection status changes. */
export function onSyncStatus(cb: StatusListener): () => void {
	_statusListeners.add(cb);
	cb(_status);
	return () => _statusListeners.delete(cb);
}

/** The last known server version number. */
export function getServerVersion(): number {
	return _version;
}

/** Current connection status. */
export function getSyncStatus(): SyncStatus {
	return _status;
}

/**
 * Fetch the full league snapshot JSON string from the API.
 * Returns null if the server has no snapshot yet.
 */
export async function fetchSnapshot(): Promise<{ json: string; version: number } | null> {
	try {
		const res = await fetch(`${API_BASE}/api/league/snapshot`);
		if (res.status === 204) return null;
		if (!res.ok) throw new Error(`HTTP ${res.status}`);
		const version = parseInt(res.headers.get("X-League-Version") ?? "0", 10);
		const json = await res.text();
		return { json, version };
	} catch (e) {
		console.warn("[WBBL sync] fetchSnapshot failed:", e);
		return null;
	}
}

/**
 * Push the full league JSON to the server (admin / commissioner only).
 * Requires an admin bearer token.
 */
export async function pushSnapshot(json: string, adminToken: string): Promise<boolean> {
	try {
		const res = await fetch(`${API_BASE}/api/league/snapshot`, {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
				Authorization: `Bearer ${adminToken}`,
			},
			body: json,
		});
		return res.ok;
	} catch {
		return false;
	}
}

/**
 * Send a signed league action to the server.
 * The user's MetaMask wallet signs the action so the server can verify ownership.
 *
 * @returns { ok, version } on success or { ok: false, error } on failure.
 */
export async function sendAction(
	action: LeagueAction,
	walletAddress: string,
): Promise<{ ok: boolean; version?: number; error?: string }> {
	const nonce = _nonce++;
	const msgToSign = JSON.stringify({ type: action.type, payload: action.payload, nonce });

	let signature: string;
	try {
		signature = await _signMessage(msgToSign, walletAddress);
	} catch (e: any) {
		return { ok: false, error: e?.message ?? "Signing failed" };
	}

	try {
		const res = await fetch(`${API_BASE}/api/league/action`, {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({
				type: action.type,
				wallet: walletAddress,
				signature,
				nonce,
				payload: action.payload,
			}),
		});
		const data = await res.json() as { ok?: boolean; version?: number; error?: string };
		if (!res.ok) return { ok: false, error: data.error ?? `HTTP ${res.status}` };
		return { ok: true, version: data.version };
	} catch (e: any) {
		return { ok: false, error: e?.message ?? "Network error" };
	}
}

/**
 * Fetch the team → owner wallet map from the server.
 */
export async function fetchClaims(): Promise<Record<string, string>> {
	try {
		const res = await fetch(`${API_BASE}/api/league/claims`);
		if (!res.ok) return {};
		return res.json() as Promise<Record<string, string>>;
	} catch {
		return {};
	}
}

// ── internal helpers ──────────────────────────────────────────────────────────

async function _fetchVersion(): Promise<void> {
	try {
		const res = await fetch(`${API_BASE}/api/league/version`);
		if (res.ok) {
			const data = await res.json() as { version: number };
			_version = data.version ?? 0;
		}
	} catch {
		// network offline — keep last known version
	}
}

function _connectWs() {
	if (_ws) return;
	const wsBase = API_BASE.replace(/^http/, "ws");
	_setStatus("connecting");

	const ws = new WebSocket(`${wsBase}/api/league/live`);
	_ws = ws;

	ws.onopen = () => {
		_setStatus("live");
		if (_reconnectTimer) { clearTimeout(_reconnectTimer); _reconnectTimer = null; }
	};

	ws.onmessage = (evt) => {
		try {
			const msg = JSON.parse(evt.data as string) as {
				type: string;
				version?: number;
			};
			if (msg.type === "hello" || msg.type === "snapshot_updated" || msg.type === "action_applied") {
				if (msg.version !== undefined && msg.version > _version) {
					_version = msg.version;
					_notifyUpdate(_version);
				}
			}
			if (msg.type === "team_claimed") {
				_notifyUpdate(_version);
			}
		} catch {
			// ignore malformed messages
		}
	};

	ws.onerror = () => {
		_setStatus("stale");
	};

	ws.onclose = () => {
		_ws = null;
		_setStatus("offline");
		// Exponential back-off reconnect: 3s, 6s, 12s … capped at 60s
		const delay = Math.min(60_000, 3_000 * 2 ** Math.min(_reconnectAttempts++, 4));
		_reconnectTimer = setTimeout(() => {
			_reconnectTimer = null;
			_connectWs();
		}, delay);
	};
}

let _reconnectAttempts = 0;

function _setStatus(s: SyncStatus) {
	if (_status === s) return;
	_status = s;
	for (const cb of _statusListeners) cb(s);
}

function _notifyUpdate(version: number) {
	for (const cb of _updateListeners) cb(version);
}

/**
 * Sign a plain-text message with the connected MetaMask wallet via personal_sign.
 * Returns the hex signature string.
 */
async function _signMessage(message: string, account: string): Promise<string> {
	const eth = (window as any).ethereum;
	if (!eth) throw new Error("MetaMask not found");
	const msgHex = "0x" + Array.from(new TextEncoder().encode(message))
		.map((b) => b.toString(16).padStart(2, "0"))
		.join("");
	const sig: string = await eth.request({
		method: "personal_sign",
		params: [msgHex, account],
	});
	return sig;
}
